import { GANGING_SPEC } from '@modules/Df/TechPack/S/Constant/jogakSticker.gangSpec';
import type { Box, FitCandidate, ItemSizeInfo, Size, SizeCalculationResult } from './sizeCalculator.types';
import {
  EmptyBoxListError,
  ExcessiveMarginError,
  MinScaleFitError,
  NormalizedItemSizeNotInitializedError,
  NoViableBoxError,
} from './sizeCalculator.error';
import { JogakStickerGangingItem, PlushGangSpec } from '@modules/Df/TechPack/S/types/techPack.ts';

/** 절대 최소 변 길이(mm) - itemWithMargin (마진을 포함한 아이템 크기) 기준 */

export class ItemSizeCalculator {
  private readonly boxes: Box[];
  private normalizedItemSize: Size | null = null;
  private minSizeWidthMargin = 30;

  constructor(boxes: Box[], minSizeWidthMargin?: number) {
    if (!boxes || boxes.length === 0) {
      throw new EmptyBoxListError();
    }

    this.boxes = boxes;
    this.minSizeWidthMargin = minSizeWidthMargin ?? 30;
  }

  /**
   * 주어진 비율과 마진으로 아이템의 최소/최대 크기를 계산합니다.
   */
  public getMinMaxItemSize(aspectRatio: number, marginMm: number): SizeCalculationResult {
    this.normalizedItemSize = this.createNormalizedItemSize(aspectRatio);
    const { minItemInfo, maxItemInfo, availableBoxIds } = this.calculateMinMaxItemSize(aspectRatio, marginMm);

    return {
      item: {
        min: minItemInfo,
        max: maxItemInfo,
      },
      itemWithMargin: {
        min: {
          ...minItemInfo,
          size: {
            width: minItemInfo.size.width + marginMm * 2,
            height: minItemInfo.size.height + marginMm * 2,
          },
        },
        max: {
          ...maxItemInfo,
          size: {
            width: maxItemInfo.size.width + marginMm * 2,
            height: maxItemInfo.size.height + marginMm * 2,
          },
        },
      },
      availableBoxIds,
    };
  }

  /**
   * 주어진 실제 아이템 크기에 대해 마진을 고려하여 들어갈 수 있는 박스 정보를 반환합니다.
   */
  public getAvailableBoxesAtItemSize(
    itemSize: Size,
    marginMm: number,
  ): { boxId: string; needRotate: boolean }[] {
    // 마진을 포함한 아이템 크기 계산
    const itemWithMarginSize = {
      width: itemSize.width + marginMm * 2,
      height: itemSize.height + marginMm * 2,
    };

    // 최소 크기 요구사항 검증
    if (
      itemWithMarginSize.width < this.minSizeWidthMargin ||
      itemWithMarginSize.height < this.minSizeWidthMargin
    ) {
      throw new ExcessiveMarginError(marginMm, this.minSizeWidthMargin);
    }

    // 들어갈 수 있는 박스들 찾기
    const availableBoxes = this.boxes
      .map((box) => {
        const fitResult = this.canItemFitInBox(itemSize, box, marginMm);
        return fitResult ? { boxId: box.id, needRotate: fitResult.needRotate } : null;
      })
      .filter((result): result is { boxId: string; needRotate: boolean } => result !== null);

    return availableBoxes;
  }

  /**
   * Returns the current item size scaled by the given factor, using the last calculated normalizedItemSize.
   * Throws if getMinMaxItemSize was not called first.
   */
  public getCurrentSize(scaleFactor: number): Size {
    if (!this.normalizedItemSize) {
      throw new NormalizedItemSizeNotInitializedError();
    }
    return {
      width: this.normalizedItemSize.width * scaleFactor,
      height: this.normalizedItemSize.height * scaleFactor,
    };
  }

  /**
   * 비율 정보만으로 최소/최대 아이템 크기를 계산 (margin 고려)
   */
  private calculateMinMaxItemSize(
    aspectRatio: number,
    margin: number,
  ): { minItemInfo: ItemSizeInfo; maxItemInfo: ItemSizeInfo; availableBoxIds: string[] } {
    // 비율 정보를 정규화된 아이템 크기로 변환
    const normalizedItemSize = this.createNormalizedItemSize(aspectRatio);

    // 아이템 크기가 최소 길이 요구조건을 만족하기 위해 필요한 스케일 계산
    const minScaleRequired = this.calculateMinScaleRequired(normalizedItemSize, margin);

    // 최소 스케일에서 가장 작은 면적의 박스 찾기
    const minCandidateBox = this.findMinScaleFit(normalizedItemSize, margin, minScaleRequired);

    // 아이템이 들어갈 수 있는 박스 후보 찾기
    const viableCandidateBoxes = this.findViableCandidateBoxes(normalizedItemSize, margin, minScaleRequired);

    // 후보들 중에서 최대 스케일이 가능한 박스 찾기
    const maxCandidateBox = this.findMaxScaleCandidate(viableCandidateBoxes);

    return {
      minItemInfo: {
        boxId: minCandidateBox.minBoxId,
        size: this.calculateActualSize(normalizedItemSize, minScaleRequired),
        scale: minScaleRequired,
        rotated: minCandidateBox.minRotated,
      },
      maxItemInfo: {
        boxId: maxCandidateBox.boxId,
        size: this.calculateActualSize(normalizedItemSize, maxCandidateBox.scale),
        scale: maxCandidateBox.scale,
        rotated: maxCandidateBox.rotated,
      },
      availableBoxIds: viableCandidateBoxes.map((candidate) => candidate.boxId),
    };
  }

  /**
   * 정규화된 아이템 크기 생성 (shorter side = 1)
   */
  private createNormalizedItemSize(aspectRatio: number): Size {
    return aspectRatio >= 1 ? { width: aspectRatio, height: 1 } : { width: 1, height: 1 / aspectRatio };
  }

  /**
   * 최소 스케일 요구사항 계산
   */
  private calculateMinScaleRequired(normalizedItemSize: Size, margin: number): number {
    const requiredMinItemSide = this.minSizeWidthMargin - margin * 2;
    if (requiredMinItemSide <= 0) {
      throw new ExcessiveMarginError(margin, this.minSizeWidthMargin);
    }

    // 긴 변, 짧은 변 모두 최소 요구조건을 만족하는 스케일 계산
    return Math.max(
      requiredMinItemSide / normalizedItemSize.width,
      requiredMinItemSide / normalizedItemSize.height,
    );
  }

  /**
   * 유효한 후보 박스들을 찾습니다.
   */
  private findViableCandidateBoxes(
    normalizedItemSize: Size,
    margin: number,
    minScaleRequired: number,
  ): FitCandidate[] {
    // 모든 박스 크기에 최대로 들어갈 수 있는 스케일을 계산하고, 최소 스케일 요구조건을 만족하는 박스만 필터링
    const candidates = this.boxes
      .map((box) => {
        const fit = this.getMaxScaleFitInsideBox(normalizedItemSize, box, margin);
        return fit && fit.scale >= minScaleRequired ? { ...fit, boxId: box.id, boxSize: box.size } : null;
      })
      .filter((candidate): candidate is FitCandidate => candidate !== null);

    if (candidates.length === 0) {
      const requiredMinItemSide = this.minSizeWidthMargin - margin * 2;
      throw new NoViableBoxError(margin, this.minSizeWidthMargin, requiredMinItemSide);
    }

    return candidates;
  }

  /**
   * 최소 스케일에서 맞는 박스와 회전 여부를 찾습니다.
   * 조건을 만족하는 박스들 중 면적이 가장 작은 박스를 선택합니다.
   */
  private findMinScaleFit(
    normalizedItemSize: Size,
    margin: number,
    minScaleRequired: number,
  ): { minBoxId: string; minRotated: boolean } {
    const validBoxes: { box: Box; rotated: boolean; area: number }[] = [];

    for (const box of this.boxes) {
      const fitAtMinScale = this.canFitInBoxAtScale(normalizedItemSize, box, margin, minScaleRequired);
      if (fitAtMinScale?.fits) {
        const area = box.size.width * box.size.height;
        validBoxes.push({
          box,
          rotated: fitAtMinScale.rotated,
          area,
        });
      }
    }

    if (validBoxes.length === 0) {
      throw new MinScaleFitError(this.minSizeWidthMargin, margin, minScaleRequired);
    }

    // 면적이 가장 작은 박스 선택
    const smallestAreaBox = validBoxes.reduce((prev, current) => (current.area < prev.area ? current : prev));

    return {
      minBoxId: smallestAreaBox.box.id,
      minRotated: smallestAreaBox.rotated,
    };
  }

  /**
   * 최대 스케일 후보를 찾습니다.
   */
  private findMaxScaleCandidate(viableCandidates: FitCandidate[]): FitCandidate {
    return viableCandidates.reduce((prev, current) => (current.scale > prev.scale ? current : prev));
  }

  /**
   * 정규화된 아이템의 스케일을 실제 크기로 변환합니다.
   */
  private calculateActualSize(normalizedItem: Size, scale: number): Size {
    const scaledWidth = normalizedItem.width * scale;
    const scaledHeight = normalizedItem.height * scale;

    return { width: scaledWidth, height: scaledHeight };
  }

  /**
   * 특정 박스에 정규화된 아이템을 margin을 두고 넣을 때 허용되는 '최대' 스케일
   */
  private getMaxScaleFitInsideBox(
    normalizedItem: Size,
    box: Box,
    margin: number,
  ): { scale: number; rotated: boolean } | null {
    // 패딩을 고려하여 사용 가능한 너비, 높이 계산
    const availableWidth = box.size.width - margin * 2;
    const availableHeight = box.size.height - margin * 2;

    // 사용 가능한 너비, 높이가 0 이하면 넣을 수 없음
    if (availableWidth <= 0 || availableHeight <= 0) return null;

    // 회전하지 않고 들어갈 수 있는 스케일 계산 (가로, 세로 모두 box 크기 내에 들어갈 수 있는 스케일 계산)
    const scaleWithoutRotation = Math.min(
      availableWidth / normalizedItem.width,
      availableHeight / normalizedItem.height,
    );

    // 회전해서 들어갈 수 있는 스케일 계산 (가로, 세로 모두 box 크기 내에 들어갈 수 있는 스케일 계산)
    const scaleWithRotation = Math.min(
      availableWidth / normalizedItem.height,
      availableHeight / normalizedItem.width,
    );

    if (scaleWithoutRotation <= 0 && scaleWithRotation <= 0) return null;

    // 회전해서 들어갈 수 있는 스케일이 더 크면 회전하여 넣음
    return scaleWithRotation > scaleWithoutRotation
      ? { scale: scaleWithRotation, rotated: true }
      : { scale: scaleWithoutRotation, rotated: false };
  }

  /**
   * 특정 스케일로 정규화된 아이템이 박스에 들어갈 수 있는지 확인
   */
  private canFitInBoxAtScale(
    normalizedItem: Size,
    box: Box,
    margin: number,
    scale: number,
  ): { fits: boolean; rotated: boolean } | null {
    const itemWidth = normalizedItem.width * scale;
    const itemHeight = normalizedItem.height * scale;
    const availableWidth = box.size.width - margin * 2;
    const availableHeight = box.size.height - margin * 2;

    if (availableWidth <= 0 || availableHeight <= 0) return null;

    // 회전하지 않고 들어갈 수 있는 경우
    if (itemWidth <= availableWidth && itemHeight <= availableHeight) {
      return { fits: true, rotated: false };
    }

    // 회전해서 들어갈 수 있는 경우
    if (itemHeight <= availableWidth && itemWidth <= availableHeight) {
      return { fits: true, rotated: true };
    }

    return { fits: false, rotated: false };
  }

  /**
   * 실제 아이템 크기가 박스에 마진을 고려하여 들어갈 수 있는지 확인하고 회전 여부를 반환
   */
  private canItemFitInBox(itemSize: Size, box: Box, margin: number): { needRotate: boolean } | null {
    const availableWidth = box.size.width - margin * 2;
    const availableHeight = box.size.height - margin * 2;

    if (availableWidth <= 0 || availableHeight <= 0) return null;

    // 회전하지 않고 들어갈 수 있는 경우
    if (itemSize.width <= availableWidth && itemSize.height <= availableHeight) {
      return { needRotate: false };
    }

    // 회전해서 들어갈 수 있는 경우
    if (itemSize.height <= availableWidth && itemSize.width <= availableHeight) {
      return { needRotate: true };
    }

    return null;
  }
}

/**
 * 조각 스티커 조판용 ItemSizeCalculator 인스턴스를 생성
 */
export function createJogakStickerGangCalculator(
  sizeSpecs: Record<string, JogakStickerGangingItem | PlushGangSpec>,
  minSizeWidthMargin: number,
): ItemSizeCalculator {
  const boxes = Object.entries(sizeSpecs)
    .filter(([, { is_hidden }]) => !is_hidden)
    .map(([key, spec]) => ({
      id: key, // 박스의 고유 이름
      size: spec.bounding_box_size_mm, // 박스의 가로 세로 크기
    }));
  return new ItemSizeCalculator(boxes, minSizeWidthMargin);
}

export { GANGING_SPEC };

// Re-export error classes for external error handling
export {
  SizeCalculatorError,
  EmptyBoxListError,
  ExcessiveMarginError,
  NoViableBoxError,
  MinScaleFitError,
  NormalizedItemSizeNotInitializedError,
} from './sizeCalculator.error';
